import { createAsyncThunk } from '@reduxjs/toolkit';
import getConfig from 'next/config';
import * as R from 'ramda';

import { trackLoop54Event } from 'src/redux/loop54';
import {
  AddToBasketResponse,
  GetBasketWithOptionsResponse,
  NextConfig,
  ProductOptionInput,
  ProductOptionValue,
} from 'src/types';
import { ReduxApplicationState } from '../combinedReducer';
import { SelectedPersonalisationsRecord } from '../personalisation';
import * as queries from './graphql-queries';

const { publicRuntimeConfig } = getConfig() as NextConfig;
const BASKET_GRAPHQL_ENDPOINT = publicRuntimeConfig.endpoints.basket;

type ProductOptionInputWithoutLabel = Pick<ProductOptionInput, 'optionId' | 'value' | 'valueId'>[];

export interface QueryVariables {
  id?: string;
  code?: string;
  quantity: number;
  options: ProductOptionInputWithoutLabel;
}
export interface QueryBody {
  body: {
    query: string;
    variables?: QueryVariables;
  };
}

const basketAPI = {
  endpoint: BASKET_GRAPHQL_ENDPOINT,
  name: 'basket-api',
};

const dispatchBasketEvent = () => {
  const event = new Event('basketUpdated');
  document.dispatchEvent(event);
};

const graphqlRequest = async <T>(query: string, variables?: QueryVariables) => {
  window.Amplify.Configure.API.REST.addEndpoint(basketAPI);

  return await window.Amplify.default.API.post<QueryBody, T>(basketAPI.name, '/graphql', {
    body: {
      query,
      variables,
    },
  });
};

const mapProductOptionsToBasketOptions = (
  selectedPersonalisations: SelectedPersonalisationsRecord,
): ProductOptionInputWithoutLabel =>
  R.chain(
    (optionId) =>
      /* istanbul ignore next */
      selectedPersonalisations?.[optionId]?.map(({ id, name, isCustomInput }: ProductOptionValue) =>
        isCustomInput ? { optionId, value: name } : { optionId, valueId: String(id) },
      ) ?? [],
    Object.keys(selectedPersonalisations),
  );

export const addItemToBasket = createAsyncThunk<AddToBasketResponse>(
  'basket/addItemToBasket',
  async (_, { getState, dispatch }) => {
    const {
      product: { code, quantity },
      personalisation: { selectedPersonalisations },
      userConfiguration: { currencyCode },
    } = getState() as ReduxApplicationState;
    const options = mapProductOptionsToBasketOptions(selectedPersonalisations);

    const apiResponse = await graphqlRequest<AddToBasketResponse>(
      queries.addItemToBasketMutation(currencyCode ?? 'GBP'),
      {
        code: String(code),
        quantity,
        options,
      },
    );

    dispatchBasketEvent();

    dispatch(trackLoop54Event({ eventType: 'addtocart' }));

    return apiResponse;
  },
);

export const updateItem = createAsyncThunk<AddToBasketResponse>(
  'basket/updateItem',
  async (_, { getState }) => {
    const {
      product: { quantity },
      personalisation: { editedItem, selectedPersonalisations },
      userConfiguration: { currencyCode },
    } = getState() as ReduxApplicationState;

    const options = mapProductOptionsToBasketOptions(selectedPersonalisations);

    const apiResponse = await graphqlRequest<AddToBasketResponse>(
      queries.updateItemMutation(currencyCode ?? 'GBP'),
      {
        id: editedItem as string,
        quantity,
        options,
      },
    );

    dispatchBasketEvent();

    return apiResponse;
  },
);

export const getBasketWithOptions = createAsyncThunk<GetBasketWithOptionsResponse>(
  'basket/getBasketWithOptions',
  async () =>
    await graphqlRequest<GetBasketWithOptionsResponse>(
      queries.getProductCustomisationOptionsQuery(),
    ),
);
